import {
	REQUEST,
	SUCCESS,
	FAILED,
	LOGGED_OUT,
	AVATAR_UPDATED,
	CLEAR_AVATAR,
	LOGO_UPDATED,
	CLEAR_ERRORS,
	REGISTER_SUCCESS,
	GOOGLE_AUTHENTICATED,
	GOOGLE_AUTH_FAILED,
	MAIL_RETRIEVED,
	GOOGLE_LOGOUT,
	CHANGE_DESK_IMAGE,
	UPDATE_ACHIEVEMENTS,
	SWITCH_COMPANY_REQUEST,
	SWITCH_COMPANY_SUCCESSFUL,
	SWITCH_COMPANY_FAILED,
	HOLD_REGISTER_STATE_VALUES,
	CLEAR_REGISTER_STATE_VALUES,
	CLEAR_MAIL_AUTH,
	GOOGLE_SIGNIN,
	AWAITINGMFA,
	UPDATE_USER,
	UPDATE_GOOGLE_ACCOUNT,
	GOOGLE_CALENDARS_ADDED,
	ADD_CALENDAR_TOKEN,
	RESET_APP,
	REMOVE_INTEGRATION,
	HANDLE_FAVOURITE,
	REFRESH,
	ADD_INTEGRATION,
	SET_TUTORIAL_COMPLETE,
	ADD_LOCAL_CALENDARS,
	ADD_LIKE_TO_POST,
	ADD_COMMENT_TO_POST,
	DELETE_COMMENT_FROM_POST,
	DELETE_LIKE_FROM_POST,
	UPDATE_GOOGLE_MAIL_TOKEN,
	REMOVE_MAIL_SCOPE,
	REFRESH_GOOGLE_TOKEN,
} from '../actions/types';
import { clearPersistState, updateObject } from './utils/globalStateFunctions';
import { FLUSH, PURGE } from 'redux-persist';
import { Platform } from 'react-native';
import { abortAllRequests } from '@utils/abortController';
import userData from '@navigation/screens/MidLevel/UsersScreen/Components/userData';

const INITIAL_STATE = {
	loading: false,
	message: {},
	isAuthenticated: false,
	success: null,
	data: {
		userData: {
			authentications: {} as { [key: string]: any },
			calendar: {},
			favourites: {
				projects: [],
				data: [],
				news: [],
			},
			posts: { liked_posts: [] },
		},
	},
};

const authReducer = (state = INITIAL_STATE, action) => {
	switch (action.type) {
		case REMOVE_INTEGRATION: {
			const integration: string = action.payload;

			// Remove the specified key from the authentications object
			const { [integration]: _, ...rest } =
				state.data.userData.authentications;

			return updateObject(state, {
				data: {
					...state.data,
					userData: {
						...state.data.userData,
						authentications: {
							...rest, // Use the updated authentications object without the removed key
						},
					},
				},
			});
		}
		case ADD_INTEGRATION:
			return updateObject(state, {
				data: {
					...state.data,
					userData: {
						...state.data.userData,
						authentications: {
							...state.data.userData.authentications,
							[action.payload.authType]: action.payload.data,
						},
					},
				},
			});
		case CLEAR_MAIL_AUTH:
			return updateObject(state, {
				data: {
					...state.data,
					userData: {
						...state.data.userData,
						authentications: {
							...state.data.userData.authentications,
							mailAuthenticated: false,
							mail: {},
						},
					},
				},
			});
		case REMOVE_MAIL_SCOPE:
			return updateObject(state, {
				data: {
					...state.data,
					userData: {
						...state.data.userData,
						authentications: {
							...state.data.userData.authentications,
							mail: {
								...state.data.userData.authentications.mail,
								mailAuthenticated: false,
							},
							[action.payload.authType]: {
								[action.payload.authType.scopes]: [
									...state.data.userData.authentications[
										action.payload.authType.scopes
									].filter(
										(scope) => scope !== action.payload
									),
								],
							},
						},
					},
				},
			});
		case UPDATE_USER:
			return updateObject(state, {
				...state,
				data: {
					userData: {
						...state.data.userData,
						...action.payload,
						// Deep merge authentications if provided
						...(action.payload.authentications && {
							authentications: {
								...state.data.userData.authentications,
								...action.payload.authentications,
							},
						}),
						// Deep merge context if provided (for onboarding data)
						...(action.payload.context && {
							context: {
								...state.data.userData.context,
								...action.payload.context,
							},
						}),
					},
				},
			});
		case UPDATE_GOOGLE_MAIL_TOKEN:
			return updateObject(state, {
				data: {
					...state.data,
					userData: {
						...state.data.userData,
						authentications: {
							...state.data.userData.authentications,
							google: {
								...state.data.userData.authentications.google,
								accessToken: action.payload,
							},
						},
					},
				},
			});
		case LOGGED_OUT:
			return updateObject(state, INITIAL_STATE);
		case HOLD_REGISTER_STATE_VALUES:
			const heldValues = action.payload.values;
			return updateObject(state, {
				heldValues: heldValues,
			});
		case CLEAR_REGISTER_STATE_VALUES:
			return updateObject(state, {
				heldValues: {},
			});
		case ADD_LIKE_TO_POST:
			// Clone the liked_posts array and push the new payload into it
			const newLikedPosts = [
				...state.data.userData.posts.liked_posts,
				action.payload,
			];

			return updateObject(state, {
				data: {
					...state.data,
					userData: {
						...state.data.userData,
						posts: {
							...state.data.userData.posts,
							liked_posts: newLikedPosts,
						},
					},
				},
			});

		case DELETE_LIKE_FROM_POST:
			const updatedLikedPosts =
				state.data.userData.posts.liked_posts.filter(
					(post) => post !== action.payload
				);

			const likedPosts =
				updatedLikedPosts.length > 0 ? updatedLikedPosts : [];

			return updateObject(state, {
				data: {
					...state.data,
					userData: {
						...state.data.userData,
						posts: {
							...state.data.userData.posts,
							liked_posts: likedPosts,
						},
					},
				},
			});
		case HANDLE_FAVOURITE:
			const { id, type, body } = action.payload;
			console.log('🎯 HANDLE_FAVOURITE:', id, type, body);

			// Determine the item to search for, using body if it exists, otherwise id
			const item = body ? body : id;

			// Get current favourites array for the type (or empty array if doesn't exist)
			const currentFavourites =
				state.data.userData.favourites?.[type] || [];

			// Check if the item already exists in the favourites array
			const existingIndex = currentFavourites.indexOf(item);

			// Create new favourites array (immutable update)
			let newFavourites;
			if (existingIndex !== -1) {
				// Item exists, remove it from the array
				newFavourites = [
					...currentFavourites.slice(0, existingIndex),
					...currentFavourites.slice(existingIndex + 1),
				];
				console.log('❌ REMOVED FAVOURITE:', item, 'from', type);
			} else {
				// Item does not exist, add it to the array
				newFavourites = [...currentFavourites, item];
				console.log('✅ ADDED FAVOURITE:', item, 'to', type);
			}

			// Return completely new state object (no mutations)
			return updateObject(state, {
				data: {
					...state.data,
					userData: {
						...state.data.userData,
						favourites: {
							...state.data.userData.favourites,
							[type]: newFavourites,
						},
					},
				},
			});
		case SET_TUTORIAL_COMPLETE:
			return updateObject(state, {
				data: {
					...state.data,
					userData: {
						...state.data.userData,
						tutorials: {
							...state.data.userData.tutorials,
							firstTutorial: true,
							tutorialModal: true, // Also set tutorialModal when firstTutorial is set
						},
					},
				},
			});
		case RESET_APP:
			return updateObject(state, INITIAL_STATE);
		case UPDATE_ACHIEVEMENTS:
			return updateObject(state, {
				data: {
					...state.data,
					userData: {
						...state.data.userData,
						achievements: {
							...state.data.userData.achievements,
							achievementPoints: action.payload,
						},
					},
				},
			});
		case REFRESH:
			return updateObject(state, {
				data: {
					...state.data,
					userData: {
						...state.data.userData,
						achievements: {
							...state.data.userData.achievements,
							achievementPoints: action.payload.achievements,
						},
						favourites: {
							...state.data.userData.favourites,
							favourites: action.payload.favourites,
						},
						profile_information: action.payload.profile_information,
						posts: {
							...state.data.userData.posts,
							posts: action.payload.posts,
						},
					},
				},
			});
		case REFRESH_GOOGLE_TOKEN:
			return updateObject(state, {
				data: {
					...state.data,
					userData: {
						...state.data.userData,
						authentications: {
							...state.data.userData.authentications,
							google: {
								...state.data.userData.authentications.google,
								accessToken: action.payload,
							},
						},
					},
				},
			});
		case UPDATE_GOOGLE_ACCOUNT:
			return updateObject(state, {
				data: {
					...state.data,
					userData: {
						...state.data.userData,
						authentications: {
							...state.data.userData.authentications,
							mail: {
								...state.data.userData.authentications.mail,
								mailAuthenticated:
									action.payload.scopes.includes(
										'https://www.googleapis.com/auth/gmail.modify'
									),
								mailType: action.payload.scopes.includes(
									'https://www.googleapis.com/auth/gmail.modify'
								)
									? 'google'
									: state.data.userData.authentications.mail
											?.mailType || '',
							},
							google: action.payload,
						},
					},
				},
			});
		// if (Platform.OS === 'web') {
		// 	window.location.reload();
		// } else {
		// 	return;
		// }
		case ADD_CALENDAR_TOKEN:
			return updateObject(state, {
				google: {
					...state.google,
					gCal: action.payload,
				},
			});
		case SWITCH_COMPANY_REQUEST:
			//Cancel all active network requests
			return updateObject(state, {
				loading: true,
			});
		case SWITCH_COMPANY_SUCCESSFUL:
			// Handle different switch types: user, company, or personal
			const switchType = action.payload?.switchType;
			if (switchType === 'user') {
				// User switch - clear companyData and update userData with target user info
				return updateObject(state, {
					loading: false,
					data: {
						...state.data,
						companyData: null, // Clear company data when switching to user
						userData: action.payload.userData
							? {
									...state.data?.userData,
									id: action.payload.userId, // Target user ID
									originalUserId:
										action.payload.originalUserId, // Original user ID
									targetUserId: action.payload.targetUserId, // Target user ID
									targetUser: action.payload.targetUser, // Target user profile info
								}
							: state.data?.userData,
					},
				});
			} else if (switchType === 'personal') {
				// Personal account switch - clear companyData and targetUser info
				const updatedUserData = state.data?.userData
					? { ...state.data.userData }
					: null;
				if (updatedUserData) {
					// Explicitly remove targetUserId and targetUser properties
					delete updatedUserData.targetUserId;
					delete updatedUserData.targetUser;
				}
				return updateObject(state, {
					loading: false,
					data: {
						...state.data,
						companyData: null,
						userData: updatedUserData || state.data?.userData,
					},
				});
			} else {
				// Company switch - update companyData
				return updateObject(state, {
					loading: false,
					data: {
						...state.data,
						companyData: action.payload.companyData,
					},
				});
			}
		case SWITCH_COMPANY_FAILED:
			return updateObject(state, {
				loading: false,
			});
		case REQUEST:
			return updateObject(state, {
				loading: true,
			});
		case MAIL_RETRIEVED:
			return updateObject(state, {
				mailRetrieved: Date.now(),
			});
		case GOOGLE_AUTH_FAILED:
			return updateObject(state, {
				google: {
					...state.google,
					googleAuthenticated: false,
					googleCredentials: null,
				},
			});
		case GOOGLE_LOGOUT:
			return updateObject(state, {
				google: {
					...state.google,
					googleAuthenticated: false,
					googleCredentials: null,
					gMail: false,
					gCal: false,
					gDrive: false,
				},
				mailRetrieved: null,
			});
		case SUCCESS:
			return updateObject(state, {
				...state,
				message: {},
				data: action.data,
				loading: false,
				isAuthenticated: true,
			});
		case AWAITINGMFA:
			return updateObject(state, {
				message: {},
				data: action.data,
				loading: false,
				isAuthenticated: false,
			});
		case CLEAR_AVATAR:
			return updateObject(state, {
				data: {
					...state.data,
					userData: {
						...state.data.userData,
						avatar: {
							...state.data.userData.avatar,
							img: undefined,
						},
					},
				},
			});
		case AVATAR_UPDATED:
			updateObject(state, {
				data: {
					...state.data,
					userData: {
						...state.data.userData,
						avatar: {
							...state.data.userData.avatar,
							img: '',
						},
					},
				},
			});
			return updateObject(state, {
				data: {
					...state.data,
					userData: {
						...state.data.userData,
						avatar: {
							...state.data.userData.avatar,
							img: action.data,
						},
					},
				},
			});
		case LOGO_UPDATED:
			return updateObject(state, {
				data: {
					...state.data,
					companyData: {
						...state.data.companyData,
						companyLogo: {
							...state.data.companyData.companyLogo,
							img: action.data,
						},
					},
				},
			});
		case CHANGE_DESK_IMAGE:
			return updateObject(state, {
				data: {
					...state.data,
					userData: {
						...state.data.userData,
						dashboardImage: {
							...state.data.userData.dashboardImage,
							img: action.data,
						},
					},
				},
			});
		case GOOGLE_AUTHENTICATED:
			return updateObject(state, {
				google: {
					...state.google,
					googleAuthenticated: true,
					googleCredentials: action.payload,
				},
			});
		case REGISTER_SUCCESS:
			return updateObject(state, {
				message: action.payload.message,
			});
		case FAILED:
			return updateObject(state, {
				error: action.payload.message,
			});
		case CLEAR_ERRORS:
			clearPersistState();
			return updateObject(state, {
				error: {},
			});
		default:
			return state;
	}
};

export default authReducer;
